<template>
    <div class="points-list-wrapper" >
        <div class="info-section">
            <el-card class="list-card">
                <div slot="header" class="list-card-header">
                    <h3>{{ $t('pointsManagement.pointList') }}</h3>
                    <div class="search-wrapper">
                        <el-input size="small" v-model="searchKeyword"
                            :placeholder="$t('pointsManagement.searchPlaceholder')" clearable @clear="clearSearch"
                            style="width: 260px">
                            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
                        </el-input>
                        <div style="margin-left: 10px;">
                            <el-button type="primary" icon="el-icon-plus" @click="$emit('add')" size="small">
                                {{ $t('pointsManagement.addPoint') }}
                            </el-button>
                        </div>
                    </div>
                </div>

                <div class="table-wrapper">
                    <el-table :data="pointsList" style="width: 100%;" @row-click="handleRowClick"
                        :row-class-name="tableRowClassName" v-loading="loading"
                        @selection-change="handleSelectionChange" ref="pointTable" highlight-current-row>
                        <el-table-column type="selection" width="45" @click.native.stop></el-table-column>
                        <el-table-column prop="WayPointName" :label="$t('pointsManagement.pointName')" min-width="120"
                            show-overflow-tooltip></el-table-column>
                        <el-table-column prop="IsEnabled" :label="$t('pointsManagement.enabled')" width="80"
                            align="center">
                            <template slot-scope="scope">
                                <el-tag size="small" :type="scope.row.IsEnabled ? 'success' : 'danger'">
                                    {{ scope.row.IsEnabled ? $t('pointsManagement.enabledPoints') :
                                        $t('pointsManagement.disabled') }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column prop="RangeRadius" :label="$t('pointsManagement.range')" width="90"
                            align="center">
                            <template slot-scope="scope">
                                <span>{{ scope.row.RangeRadius }}m</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="MaxLeaveTime" :label="$t('pointsManagement.maxLeaveTime')"
                            align="center">
                            <template slot-scope="scope">
                                <span>{{ scope.row.MaxLeaveTime }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="bindVehicleCount" :label="$t('pointsManagement.boundVehicles')"
                            width="100" align="center">
                            <template slot-scope="scope">
                                <el-tag type="info" size="small">{{ scope.row.bindVehicleCount ?
                                    scope.row.bindVehicleCount : 0 }}</el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('pointsManagement.actions')" width="180" fixed="right">
                            <template slot-scope="scope">
                                <el-button size="mini" @click.stop="$emit('bind-vehicle', scope.row)">{{
                                    $t('pointsManagement.bindVehiclesBtn') }}</el-button>
                                <el-button size="mini" @click.stop="$emit('edit', scope.row)">{{
                                    $t('pointsManagement.editBtn') }}</el-button>
                                <el-button size="mini" type="danger" @click.stop="confirmDelete(scope.row)">{{
                                    $t('pointsManagement.deleteBtn') }}</el-button>
                            </template>
                        </el-table-column>

                        <template slot="empty">
                            <div class="empty-state">
                                <i class="el-icon-place"></i>
                                <p>{{ $t('pointsManagement.emptyTip') }}</p>
                                <el-button type="primary" size="small" @click="$emit('add')">
                                    {{ $t('pointsManagement.addPoint') }}
                                </el-button>
                            </div>
                        </template>
                    </el-table>
                </div>

                <div class="pagination-wrapper">
                    <kl-pagination :total="total" :page-size="pageSize" @current-change="handleCurrentChange"
                        :current-page.sync="currentPage" @size-change="handleSizeChange">
                    </kl-pagination>
                </div>
            </el-card>
        </div>

        <el-dialog :title="$t('pointsManagement.confirmDelete')" :visible.sync="deleteDialogVisible" width="400px">
            <div class="delete-confirm-content">
                <i class="el-icon-warning" style="color: #E6A23C; font-size: 24px; margin-right: 10px;"></i>
                <span>{{ deleteMessage }}</span>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button size="small" @click="deleteDialogVisible = false">{{ $t('pointsManagement.cancelBtn') }}</el-button>
                <el-button type="danger" size="small" @click="confirmDeleteAction" :loading="deleting">{{ $t('pointsManagement.deleteBtn') }}</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { savePointsInfo } from '@/http/vehicle';

export default {
    name: 'PointsList',
    props: {
        pointsList: {
            type: Array,
            default: () => []
        },
        total: {
            type: Number,
            default: 0
        },
        loading: {
            type: Boolean,
            default: false
        },
        selectedPointId: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            searchKeyword: '',
            currentPage: 1,
            pageSize: 10,
            deleteDialogVisible: false,
            pointToDelete: null,
            pointsToDelete: null,
            selectedRows: [],
            deleting: false
        };
    },
    computed: {
        deleteMessage() {
            if (this.pointsToDelete) {
                return this.$t('pointsManagement.batchDeleteMsg', { count: this.pointsToDelete.length });
            }
            if (this.pointToDelete) {
                return this.$t('pointsManagement.deleteConfirmMsgNamed', { name: this.pointToDelete.WayPointName });
            }
            return this.$t('pointsManagement.deleteConfirmMsg');
        }
    },
    methods: {
        handleSearch() {
            this.currentPage = 1;
            this.$emit('search', { keyword: this.searchKeyword, page: this.currentPage, size: this.pageSize });
        },
        clearSearch() {
            this.searchKeyword = '';
            this.currentPage = 1;
            this.$emit('search', { keyword: this.searchKeyword, page: this.currentPage, size: this.pageSize });
        },
        handleCurrentChange(page) {
            this.currentPage = page;
            this.$emit('search', { keyword: this.searchKeyword, page: this.currentPage, size: this.pageSize });
        },
        handleSizeChange(size) {
            this.pageSize = size;
            this.currentPage = 1;
            this.$emit('search', { keyword: this.searchKeyword, page: this.currentPage, size: this.pageSize });
        },
        handleRowClick(point) {
            this.$emit('select', point);
        },
        handleSelectionChange(selection) {
            this.selectedRows = selection;
        },
        tableRowClassName({ row }) {
            return row.WayPointID === this.selectedPointId ? 'selected-row' : '';
        },
        confirmDelete(point) {
            this.pointToDelete = point;
            this.pointsToDelete = null;
            this.deleteDialogVisible = true;
        },
        confirmDeleteAction() {
            this.deleting = true;
            savePointsInfo({
                PointID: this.pointToDelete.WayPointID,
                Flag: 3
            }).then(res => {
                this.deleting = false;
                if (res.code == 200) {
                    this.deleteDialogVisible = false;
                    this.pointToDelete = null;
                    this.pointsToDelete = null;
                    this.$message.success(this.$t('pointsManagement.deleteSuccess'));
                    this.$emit('delete');
                } else {
                    this.$message.error(this.$t('pointsManagement.deleteError'));
                }
            }).catch(() => {
                this.deleting = false;
            });
        }
    }
};
</script>

<style scoped>
.points-list-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
}

.info-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.list-card {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.list-card ::v-deep .el-card__body {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    padding: 0;
}

.list-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
}

.list-card-header h3 {
    margin: 0;
    font-size: 15px;
}

.search-wrapper {
    display: flex;
    align-items: center;
}

.table-wrapper {
    flex: 1;
    overflow: auto;
    min-height: 0;
    padding: 0 20px;
}

.el-table {
    width: 100%;
}

.pagination-wrapper {
    flex-shrink: 0;
    margin-top: 12px;
    margin-bottom: 12px;
    padding: 0 20px;
    display: flex;
    justify-content: flex-end;
}

::v-deep .selected-row {
    background-color: #ecf5ff !important;
}

::v-deep .el-table__row {
    cursor: pointer;
}

.empty-state {
    padding: 40px 0;
    text-align: center;
    color: #909399;
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 12px;
    display: block;
    color: #c0c4cc;
}

.empty-state p {
    margin: 8px 0 16px;
    font-size: 14px;
}

.delete-confirm-content {
    display: flex;
    align-items: center;
    font-size: 14px;
}

@media (max-width: 1200px) {
    .points-list-wrapper {
        width: 100%;
    }
}
</style>